import axios from 'axios'
import { message } from 'antd'

// Global backend availability signal (used to show a persistent "backend restarting" banner).
export const BACKEND_STATUS_EVENT = 'emew-backend-status'
let backendDown = false
let backendDownToastShown = false

function setBackendDown(nextDown, details = {}) {
  if (backendDown === nextDown) return
  backendDown = nextDown
  // Reset toast gating once we're back up
  if (!nextDown) backendDownToastShown = false
  try {
    window.dispatchEvent(new CustomEvent(BACKEND_STATUS_EVENT, { detail: { down: backendDown, ...details } }))
  } catch (_) {
    // best-effort only
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Any successful response implies backend is available again.
    setBackendDown(false)
    return response
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response

      if (status === 401) {
        // Unauthorized - clear auth and redirect to login
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        window.location.href = '/login'
        message.error('Session expired. Please login again.')
      } else if (status === 403) {
        message.error('Access denied')
      } else if (status === 404) {
        message.error('Resource not found')
      } else if (status >= 500) {
        // Likely backend restart / temporary unavailability. Show a persistent banner via AppLayout.
        setBackendDown(true, { status })
        if (!backendDownToastShown) {
          backendDownToastShown = true
          message.warning('Backend is restarting. Please wait a moment and retry.', 3)
        }
      } else {
        message.error(data.error || 'An error occurred')
      }
    } else if (error.request) {
      // Network error (common during container restart / deploy).
      // But Axios also routes *client-side timeouts* here (ECONNABORTED). For long-running calls
      // we may want to suppress the global "backend restarting" banner/toast and let the caller
      // handle the UX.
      const isTimeout = error.code === 'ECONNABORTED'
      const suppressDown = !!error?.config?.suppressBackendDownOnTimeout
      if (!(isTimeout && suppressDown)) {
        setBackendDown(true, { status: 0 })
        if (!backendDownToastShown) {
          backendDownToastShown = true
          message.warning('Backend is restarting. Please wait a moment and retry.', 3)
        }
      }
    } else {
      message.error('An unexpected error occurred')
    }

    return Promise.reject(error)
  }
)

export default api
